import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { request } from './OperationsProvider';
import FormRow from '../common/components/FormRow';

// The mail account a plant sends its own reports from.
//
// Deliberately the plant's account rather than ours: the shift report arrives from the plant's own
// address, a reply reaches the plant, and nobody has to explain to their IT department why operating
// figures leave through a third party. The password is encrypted on the server and never comes back
// to this screen - which is why the field can be left empty to keep the one already stored.
const SECURITY = [
  ['STARTTLS', 'STARTTLS (port 587)'],
  ['SSL', 'SSL/TLS (port 465)'],
  ['NONE', 'None (internal relay)'],
];

const EmailPanel = () => {
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(false);
  const [testTo, setTestTo] = useState('');
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    request('/api/operations/email/provider')
      .then((stored) => setForm({
        host: stored.host || 'smtp.gmail.com',
        port: stored.port || 587,
        security: stored.security || 'STARTTLS',
        username: stored.username || '',
        password: '',
        fromName: stored.fromName || '',
        fromAddress: stored.fromAddress || '',
        replyTo: stored.replyTo || '',
        enabled: stored.enabled !== false,
        hasPassword: !!stored.hasPassword,
      }))
      .catch((e) => setError(e.message));
  }, []);

  const save = async (event) => {
    event.preventDefault();
    setPending(true); setError(''); setNotice('');
    try {
      const saved = await request('/api/operations/email/provider', 'PUT', { ...form, port: Number(form.port) });
      setForm((current) => ({ ...current, password: '', hasPassword: !!saved.hasPassword }));
      setNotice('Mail account saved.');
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  const sendTest = async () => {
    setPending(true); setError(''); setNotice('');
    try {
      const sent = await request('/api/operations/email/test', 'POST', { to: testTo });
      setNotice(`Test message sent to ${sent.to}.`);
    } catch (e) { setError(e.message); } finally { setPending(false); }
  };

  if (!form) return <Typography color="text.secondary" sx={{ p: 3 }}>{error || 'Loading the mail account…'}</Typography>;

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box component="form" onSubmit={save}>
        <Stack gap={2}>
          <Box>
            <Typography variant="h6">Mail account</Typography>
            <Typography variant="body2" color="text.secondary">Scheduled reports are sent from this account. Google Workspace and Microsoft 365 accounts need an app password rather than the one you sign in with.</Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {notice && <Alert severity="success">{notice}</Alert>}
          <FormRow columns="2fr 1fr 1.5fr">
            <TextField required label="Mail server" value={form.host} disabled={pending} onChange={(e) => set('host', e.target.value)} helperText="For example smtp.gmail.com" />
            <TextField required label="Port" type="number" value={form.port} disabled={pending} onChange={(e) => set('port', e.target.value)} />
            <TextField select label="Connection" value={form.security} disabled={pending} onChange={(e) => set('security', e.target.value)}>
              {SECURITY.map(([value, name]) => <MenuItem key={value} value={value}>{name}</MenuItem>)}
            </TextField>
          </FormRow>
          <FormRow>
            <TextField label="Username" value={form.username} disabled={pending} onChange={(e) => set('username', e.target.value)} helperText="Usually the full email address" />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              disabled={pending}
              onChange={(e) => set('password', e.target.value)}
              helperText={form.hasPassword ? 'A password is stored. Leave this empty to keep it.' : 'Stored encrypted; never shown again.'}
            />
          </FormRow>
          <FormRow columns="1fr 1fr 1fr">
            <TextField label="Sender name" value={form.fromName} disabled={pending} onChange={(e) => set('fromName', e.target.value)} helperText="Shown as the sender" />
            <TextField required label="Sends from" value={form.fromAddress} disabled={pending} onChange={(e) => set('fromAddress', e.target.value)} />
            <TextField label="Replies go to" value={form.replyTo} disabled={pending} onChange={(e) => set('replyTo', e.target.value)} helperText="Optional" />
          </FormRow>
          <TextField select label="Delivery" value={form.enabled ? 'on' : 'off'} disabled={pending} onChange={(e) => set('enabled', e.target.value === 'on')} sx={{ maxWidth: 260 }}>
            <MenuItem value="on">Send scheduled reports</MenuItem>
            <MenuItem value="off">Hold all mail</MenuItem>
          </TextField>
          <Stack direction="row" gap={2} alignItems="flex-start" flexWrap="wrap">
            <Button type="submit" variant="contained" disabled={pending}>{pending ? 'Working…' : 'Save mail account'}</Button>
            <TextField size="small" label="Send a test to" value={testTo} disabled={pending} onChange={(e) => setTestTo(e.target.value)} sx={{ minWidth: 260 }} helperText="Leave empty to send to yourself" />
            <Button disabled={pending} onClick={sendTest}>Send test</Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default EmailPanel;
